import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { isPayloadSourceRetrieved } from '@standardnotes/snjs';
import { NoteCollaborationPlugin } from './NoteCollaborationPlugin';
/**
 * Collaborative editing of Super notes in shared vaults, for the Super editor: the note keeps a Yjs state,
 * so concurrent edits are merged instead of duplicated (and exchanged live through the relay, if configured).
 */
export function useNoteCollaboration(controller, note, isEditorReadonly) {
    const [isCollaborative] = useState(() => Boolean(controller.item.shared_vault_uuid));
    const handle = useRef();
    /** Changing the key reloads the editor from the note, e.g. when its text was changed outside of Yjs */
    const [editorInstanceKey, setEditorInstanceKey] = useState(0);
    const onReady = useCallback((collaborationHandle) => {
        handle.current = collaborationHandle;
    }, []);
    const handleEditorChange = useCallback((value, preview, isCollaborationChange) => {
        var _a;
        /**
         * Changes of other people (live or merged during sync) are saved by their authors, and merged texts
         * of notes are rendered by CollaborativeNoteTextRenderer; saving them here would make every client
         * save every keystroke of everyone else.
         */
        if (isEditorReadonly || isCollaborationChange || !handle.current) {
            return;
        }
        const collaborationHandle = handle.current;
        const isUnchanged = value === note.current.text && !((_a = note.current.myRadmitClaudeCollaboration) === null || _a === void 0 ? void 0 : _a.textStale);
        if (isUnchanged) {
            return;
        }
        void controller.saveAndAwaitLocalPropagation({
            text: value,
            isUserModified: true,
            previews: {
                previewPlain: preview,
                previewHtml: undefined,
            },
            customMutate: (mutator) => {
                /**
                 * Read when saving (after the debounce), not in this change callback: Lexical calls the editor's change
                 * listener before the collaboration plugin copies the change into Yjs, so the state read here would
                 * miss the latest change (e.g. the last typed letter).
                 */
                mutator.myRadmitClaudeCollaboration = collaborationHandle.getState(value);
            },
        });
    }, [controller, isEditorReadonly, note]);
    const handleNoteChange = useCallback((updatedNote, source) => {
        var _a, _b;
        const merged = (_b = (_a = handle.current) === null || _a === void 0 ? void 0 : _a.applyNoteState(updatedNote)) !== null && _b !== void 0 ? _b : false;
        const textChangedOutside = !merged && updatedNote.text !== note.current.text;
        note.current = updatedNote;
        if (textChangedOutside && isPayloadSourceRetrieved(source)) {
            setEditorInstanceKey((key) => key + 1);
        }
    }, [note]);
    /** Stable between renders; the plugin is recreated with the current note when the editor is reloaded */
    return useMemo(() => ({
        isCollaborative,
        editorInstanceKey,
        handleEditorChange,
        handleNoteChange,
        plugin: isCollaborative ? _jsx(NoteCollaborationPlugin, { note: note.current, onReady: onReady }) : null,
    }), [isCollaborative, editorInstanceKey, handleEditorChange, handleNoteChange, onReady, note]);
}
